import {pickDefined, range} from '@benchling/js-utils';
import Backbone from 'backbone';
import {fromJS} from 'immutable';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import {AlignmentComments} from 'analyses/alignment/AlignmentComments';
import {assert} from 'Asserts';
import * as BioJS from 'BioJS';
import {GapSequence} from 'BioJS';
import {GAP, NucleotideType, StandardDNABase} from 'biojs/BioConstants';
import {Casing} from 'biojs/Sequence';
import {sanitize} from 'biojs/Util';
import {ComponentsSequence} from 'components/Sequence';
import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {
  DNAAlignmentAnalysisJSONAttributes,
  DNAAlignmentJSONAttributes,
  DNAAlignmentKind,
  Trace,
} from 'constants/AlignmentConstants';
import * as SequenceAnalysisConstants from 'constants/SequenceAnalysisConstants';
import {SequenceType} from 'constants/SequenceConstants';
import deepCopy from 'lib/deepCopy';
import {EnzymePreferences, Provider} from 'molbio/enzymes/EnzymeConstants';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import * as sequtil from 'molbio/SeqUtil';
import preferences from 'Preferences';
import {omit} from 'util/omitKey';
import {Optional} from 'util/Types';

export interface ImmutableWrapper<T> {
  toJS(): T;
}

interface BenchlingSaveOptions extends Backbone.ModelSaveOptions {
  debounce?: {namespace: string; wait: number};
  respondDiff?: boolean;
  fileUpdate?: boolean;
  singleton?: {namespace: string};
  skipFileUpdateXHRCount?: boolean;
  skipTimeoutXHR?: boolean;
}

export interface AnalysisAttributes {
  created_at: string;
  type: string;
}

interface JSONOptions {
  patch?: boolean;
  pickSequenceFields?: Array<string>;
  url?: string;
}

export abstract class Analysis extends Backbone.Model {
  defaults(): AnalysisAttributes {
    return {
      created_at: new Date().toISOString(),
      type: 'analyses',
    };
  }

  static parseAnalysis(analysis: {
    analysis_type: SequenceAnalysisConstants.AnalysisType;
  }): DigestAnalysis | AlignmentAnalysis | TraceAnalysis {
    switch (analysis.analysis_type) {
      case 'digest':
        return new DigestAnalysis(analysis, {parse: true});
      case 'alignment':
        return new AlignmentAnalysis(analysis, {parse: true});
      case 'trace':
        return new TraceAnalysis(analysis, {parse: true});
      default:
        throw new Error(`Unsupported analysis type: ${analysis.analysis_type}`);
    }
  }

  abstract parse(response: AnalysisAttributes): unknown;

  omitted(): Array<string> {
    return [];
  }

  toJSON(options?: JSONOptions): Partial<AnalysisAttributes> {
    return omit(this.attributes, ...this.omitted());
  }
}

interface DigestAnalysisAttributes extends AnalysisAttributes {
  analysis_type: typeof SequenceAnalysisConstants.DIGEST;
  provider: string;
}

export class DigestAnalysis extends Analysis {
  defaults(): DigestAnalysisAttributes {
    return {
      ...super.defaults(),
      analysis_type: SequenceAnalysisConstants.DIGEST,
      provider: preferences.get(EnzymePreferences.PROVIDER),
    };
  }

  provider(): Provider {
    return EnzymeUtil.getPreferredProviderForEnzyme(this.get('enzymes'), this.get('provider'));
  }

  omitted(): Array<string> {
    return ['fragments'];
  }

  parse(response: {fragments: Optional<Array<unknown>>} & AnalysisAttributes): {} {
    // FIXME: migration that removes fragments from digests. These are recomputed on render/sequence change.
    if (response.fragments != null) {
      delete response.fragments;
    }
    return response;
  }
}

interface TraceAnalysisAttributes extends AnalysisAttributes {
  analysis_type: typeof SequenceAnalysisConstants.TRACE;
}

/**
 * @deprecated: This class appears to be not applicable/unused
 */
class TraceAnalysis extends Analysis {
  defaults(): TraceAnalysisAttributes {
    return {...super.defaults(), analysis_type: SequenceAnalysisConstants.TRACE};
  }

  parse(response: unknown): unknown {
    return response;
  }
}

interface AlignmentAnalysisAttributes
  extends Omit<DNAAlignmentAnalysisJSONAttributes, 'alignments' | 'alignment_comments'> {
  alignments: AlignmentCollection;
  alignmentComments: AlignmentComments;
  analysis_type: typeof SequenceAnalysisConstants.ALIGNMENT;
}

export interface AlignmentAnalysis {
  // We monkey-patch Backbone's sync method. These options are globally applicable don't seem to be
  // respected when added in global.d.ts
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  save(attributes?: any, options?: BenchlingSaveOptions): any;
  get<K extends keyof AlignmentAnalysisAttributes>(key: K): AlignmentAnalysisAttributes[K];
  set<K extends keyof AlignmentAnalysisAttributes>(
    attributeName: K,
    value: AlignmentAnalysisAttributes[K],
    options?: Backbone.ModelSetOptions
  ): AlignmentAnalysis;
  set(obj: Partial<AlignmentAnalysisAttributes>): AlignmentAnalysis;
}

export class AlignmentAnalysis extends Analysis {
  collection!: AlignmentCollection;

  isReallyBig(): boolean {
    return this.get('alignments').length > 30;
  }

  defaults(): AlignmentAnalysisAttributes {
    return {
      ...super.defaults(),
      analysis_type: SequenceAnalysisConstants.ALIGNMENT,
      // @ts-expect-error: Initially null
      alignmentType: null,
      // @ts-expect-error: Initially null
      alignments: null,
      attachments: [],
      // @ts-expect-error: Initially null
      versionId: null,
      // @ts-expect-error: Initially null
      name: null,
      // @ts-expect-error: Initially null
      alignmentComments: null,
    };
  }

  omitted(): Array<keyof AlignmentAnalysisAttributes> {
    return ['alignmentComments', 'attached_file_count'];
  }

  name(): string | null {
    return this.get('name');
  }

  initialize(models: unknown, options: unknown): void {
    this.registerListeners();
  }

  registerListeners(): void {
    const addListener = (): void => {
      this.listenTo(
        this.get('alignments'),
        'sequenceVersionUpdate',
        (alignment: Alignment, options: {versionId: null | number}) => {
          assert(options.versionId != null, 'sequenceVersionUpdate triggered without versionId');
          this.set({versionId: options.versionId});
        }
      );
    };
    this.listenTo(this, 'change:alignments', addListener);
    if (this.get('alignments') != null) {
      addListener();
    }
  }

  getMasterSequence(): GapSequence {
    return this.get('alignments')
      .findWhere({type: this.get('alignmentType')})
      .get('sequence');
  }

  clustalFormat(): string {
    let file = '';
    const addLine = (string: string): void => {
      file += `${string}\n`;
    };
    addLine(`CLUSTALW ${this.name()}`);
    addLine('');

    const alignments = this.get('alignments').filter((al: Alignment) => al.get('type') !== 'consensus');
    if (alignments.length === 0) {
      return '';
    }

    const colLength = 3 + _.max(alignments.map((al: Alignment) => al.get('sequence').name().length));
    const seqLength = this.get('alignments').seqLength();

    for (const j of range(0, Math.floor(seqLength / 60))) {
      const start = j * 60;
      const end = Math.min(start + 60, seqLength);
      alignments.forEach((alignment: Alignment) => {
        const seq = alignment.get('sequence');
        const seqName = seq.name().replace(' ', '_');
        addLine(
          `${
            seqName + Array(1 + colLength - seqName.length).join(' ') + seq.bases().slice(start, end)
          } ${end}`
        );
      });
      let conservation = '';
      for (const i of range(start, end - 1)) {
        if (
          _.unique(alignments.map((al: Alignment) => al.get('sequence').bases()[i].toLowerCase())).length ===
          1
        ) {
          conservation += '*';
        } else {
          conservation += ' ';
        }
      }
      addLine(Array(1 + colLength).join(' ') + conservation);
      addLine('');
    }
    return file;
  }

  fastaFormat(): string {
    let file = '';
    const addLine = (string: string): void => {
      file += `${string}\n`;
    };

    const alignments = this.get('alignments').filter((al: Alignment) => al.get('type') !== 'consensus');
    const seqLength = this.get('alignments').seqLength();
    for (const alignment of alignments) {
      const sequence = alignment.get('sequence');
      const bases = sequence.bases();
      addLine(`>${alignment.get('sequence').name()}`);
      for (let i = 0, end1 = seqLength / 80, asc = 0 <= end1; asc ? i < end1 : i > end1; asc ? i++ : i--) {
        const start = i * 80;
        const end = Math.min(start + 80, seqLength);
        addLine(bases.slice(start, end));
      }
    }
    return file;
  }

  setVotes(alignmentType: DNAAlignmentKind, alignments: AlignmentCollection): Alignment {
    const consensus = alignments.findWhere({type: alignmentType});
    const others = alignments.models.filter((alignmentModel) => alignmentModel !== consensus);
    const voters = others.map((alignment) => alignment.get('sequence'));
    consensus.set({
      votes: new VoteData({sequence: consensus.get('sequence'), voterSequences: voters}),
    });
    return consensus;
  }

  parse(
    response: DNAAlignmentAnalysisJSONAttributes,
    options?: {parse?: boolean}
  ): AlignmentAnalysisAttributes | null {
    if (options && options.parse != null && !options.parse) {
      return null;
    }

    if (response.alignments != null && response.alignments.length === 0) {
      // @ts-expect-error: Should not override response
      response.alignments = null;
    }
    if (response.alignments != null) {
      // @ts-expect-error: Should not override response
      response.alignments = new AlignmentCollection(
        Array.from(response.alignments).map((alignment) => new Alignment(alignment, {parse: true}))
      );
      // @ts-expect-error: Should not override response
      this.setVotes(response.alignmentType, response.alignments);
    }
    // @ts-expect-error: Should not override response
    response.alignmentComments = new AlignmentComments(response.alignment_comments || [], {
      analysisID: response.id,
    });
    // @ts-expect-error: Should not override response
    delete response.alignment_comments;
    // @ts-expect-error: Should not override response
    return response;
  }

  unload(): void {
    this.stopAllListeners();
  }

  getUpdateJSONURL(): string {
    return `${this.url()}/json`;
  }

  toJSON({
    patch,
    pickSequenceFields,
    url,
  }: JSONOptions): Partial<AlignmentAnalysisAttributes & {patch_json: boolean}> {
    const res = super.toJSON();
    if (url === this.getUpdateJSONURL()) {
      // Since this directly sets model.json on the server, omit the other model fields.
      // super() already omits the alignmentComments field.
      // Keep fields in sync with SequenceAnalysis.to_dict() on the server.
      return omit(
        res,
        ...(['analysis_type', 'created_at', 'id', 'name', 'sequence_id'] as Array<keyof AnalysisAttributes>)
      );
    }
    if (pickSequenceFields == null) {
      // @ts-expect-error: Should not override data
      res.patch_json = false;
      return res;
    }
    // Specifying pickSequenceFields sends only sequences over with the indicated fields,
    // e.g. fields = ['name', 'circular']. Only works with the PATCH endpoint.
    assert(patch!, 'Expected patch to be true if pickSequenceFields specified');
    const alignments = [];
    // @ts-expect-error: Alignments is expected from the super call
    for (let alignment of res.alignments.toJSON()) {
      alignment = pickDefined(alignment, 'sequence');
      alignment.sequence = pickDefined(alignment.sequence.toJSON(), ...Array.from(pickSequenceFields));
      alignments.push(alignment);
    }
    const data = {alignments};
    // @ts-expect-error: Should not override data
    data.patch_json = true;
    // @ts-expect-error: Json return alignments is not an alignment collection
    return data;
  }

  toHistoryState(): ImmutableWrapper<DNAAlignmentAnalysisJSONAttributes> {
    // @ts-expect-error JSON is incorrect
    return fromJS(deepCopy(this.toJSON({})));
  }

  stopAllListeners(): void {
    this.stopListening();
    const alignments = this.get('alignments');
    if (alignments != null) {
      alignments.stopAllListeners();
    }
  }
}

interface AlignmentAttributes extends Omit<DNAAlignmentJSONAttributes, 'trace' | 'sequence'> {
  sequence: GapSequence;
  trace?: TraceData | null;
  votes?: VoteData | null; // Only set for consensus/template rows.
}

export interface Alignment {
  get<K extends keyof AlignmentAttributes>(key: K): AlignmentAttributes[K];
  set<K extends keyof AlignmentAttributes>(
    attributeName: K,
    value: AlignmentAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Alignment;
  set(obj: Partial<AlignmentAttributes>): Alignment;
}

export class Alignment extends Backbone.Model {
  attributes!: DNAAlignmentJSONAttributes;

  defaults(): AlignmentAttributes {
    return {
      // @ts-expect-error: Initially null
      trimStart: null,
      // @ts-expect-error: Initially null
      trimEnd: null,
      // @ts-expect-error: Initially null
      sequence: null,
      trace: null,
      votes: null,
      // @ts-expect-error: Initially null
      type: null,
      // @ts-expect-error: Initially null
      existingSequence: null,
    };
  }

  stopAllListeners(): void {
    this.stopListening();
    this.get('sequence').stopAllListeners();
  }

  initialize(attributes: {}, options: {}): void {
    this.listenTo(this.get('sequence'), 'sequenceVersionUpdate', function (...params: Array<unknown>) {
      // @ts-expect-error Unbound this
      this.trigger('sequenceVersionUpdate', this, ...params);
    });
    this.listenTo(this.get('sequence'), 'insert', function (...params: Array<unknown>) {
      // @ts-expect-error Unbound this
      this.trigger('insert', this, ...params);
    });
    this.listenTo(this.get('sequence'), 'delete', function (...params: Array<unknown>) {
      // @ts-expect-error Unbound this
      this.trigger('delete', this, ...params);
    });
    this.listenTo(this.get('sequence'), 'replace', function (...params: Array<unknown>) {
      // @ts-expect-error Unbound this
      this.trigger('replace', this, ...params);
    });
    this.listenTo(this.get('sequence'), 'toggleCase', function (...params: Array<unknown>) {
      // @ts-expect-error Unbound this
      this.trigger('toggleCase', this, ...params);
    });
    this.listenTo(this.get('sequence'), 'save', function () {
      // @ts-expect-error Unbound this
      this.collection.trigger('save');
    });
    this.listenTo(this.get('sequence'), 'updateTrim', function (start: number, end: number) {
      // @ts-expect-error Unbound this
      this.set({trimStart: start, trimEnd: end});
      // @ts-expect-error Unbound this
      this.trigger('debouncedUpdate');
    });
  }

  parse(response: DNAAlignmentJSONAttributes): AlignmentAttributes {
    // We had a period of time where translations could get created with end = null (T7271).
    // The migration to fix all of them is difficult, so we just don't display any translations
    // with invalid indices. To be safe, we also don't assume sequence.translations is defined
    // (not sure if that's necessary).
    // @ts-expect-error: Should not mutate response
    response.sequence.translations =
      response.sequence.translations != null
        ? response.sequence.translations.filter(({end}) => end != null)
        : undefined;

    const sequenceAttributesWithInferredNucleotideType = {
      ...response.sequence,
      type: response.sequence.sequence_type,
      nucleotideType:
        response.sequence.sequence_type === SequenceType.DOUBLE_STRANDED
          ? NucleotideType.DNA
          : NucleotideType.RNA,
    };
    // @ts-expect-error: Should not mutate response
    response.sequence = new BioJS.GapSequence(sequenceAttributesWithInferredNucleotideType, {parse: true});
    if (response.trace != null) {
      // @ts-expect-error: Should not mutate response
      response.trace.alignedSequence = response.sequence;
    }
    // @ts-expect-error: Should not mutate response
    response.trace = response.trace != null ? new TraceData(response.trace) : undefined;
    // @ts-expect-error: Should return a new object instead of mutating response
    return response;
  }

  hasDataAt(index: number): boolean {
    const trace = this.get('trace');
    return this.get('sequence').bases()[index] !== GAP || (trace != null && trace.hasDataAt(index));
  }

  toJSON(): DNAAlignmentJSONAttributes {
    // @ts-expect-error JSON is incorrect
    return omit(this.attributes, ...['alignmentComments', 'votes']);
  }
}

export class AlignmentCollection extends Backbone.Collection<Alignment> {
  debouncedUpdate!: () => void;

  static initClass(): void {
    this.prototype.model = Alignment;
  }

  initialize(models: Array<Alignment>): void {
    this.listenTo(this, 'delete', this.handleDelete.bind(this));
    this.listenTo(this, 'insert', this.handleInsert.bind(this));
    this.listenTo(this, 'replace', this.handleReplace.bind(this));
    this.listenTo(this, 'toggleCase', this.handleToggleCase.bind(this));
    this.listenTo(this, 'destroy', this.handleAlignmentDeletion.bind(this));
    this.debouncedUpdate = debounce(this.update.bind(this), 400);
    this.listenTo(this, 'debouncedUpdate', this.debouncedUpdate);
  }

  stopAllListeners(): void {
    this.stopListening();
    this.each((alignment) => {
      alignment.stopAllListeners();
    });
  }

  update(alignment: Alignment): void {
    this.trigger('update');
    if (alignment != null) {
      alignment.trigger('updateRow');
    }
  }

  invalidateLineRanges(): void {
    // Call this before any method that modifies bases.
    // This will invalidate the line ranges, and because
    // each sequence will get rerendered, it will correctly
    // render the line ranges.
    Array.from(this.models).map((alignment) => alignment.get('sequence').trigger('invalidateLineRanges'));
  }

  renderLineRanges(alignment: Alignment): void {
    // Call this when we need to force the views to rerender
    // their line ranges when the sequence doesn't change,
    // but the lines are wrong (ie. when the consensus/template
    // is modified with a strict replace).
    if (['consensus', 'template'].includes(alignment.get('type'))) {
      const result = [];
      for (const model of Array.from(this.models)) {
        if (model !== alignment) {
          result.push(model.get('sequence').trigger('renderLineRanges'));
        } else {
          result.push(undefined);
        }
      }
    }
  }

  handleAlignmentDeletion(model: Alignment): void {
    this._handleRemovingGaps(0, this.seqLength());
    model.get('sequence').trigger('delete', model.get('sequence'));
  }

  seqLength(): number {
    return this.at(0).get('sequence').bases().length;
  }

  handleToggleCase(alignment: Alignment, start: number, end: number, casing: Casing, options?: {}): void {
    if (options == null) {
      options = {};
    }
    alignment.get('sequence').changeCase(start, end, casing);
    this.update(alignment);
  }

  handleInsert(alignment: Alignment, pos: number, bases: string, options?: {}): void {
    if (options == null) {
      options = {};
    }
    this.invalidateLineRanges();
    this._handleInsert(alignment, pos, bases, options);
    alignment.get('sequence').trigger('revote');
    this.update(alignment);
  }

  handleDelete(alignment: Alignment, start: number, end: number, options?: {}): void {
    if (options == null) {
      options = {};
    }
    this.invalidateLineRanges();
    this._handleDelete(alignment, start, end);
    this._handleRemovingGaps(start, end);
    this.renderLineRanges(alignment);
    alignment.get('sequence').trigger('revote');
    this.update(alignment);
  }

  handleReplace(alignment: Alignment, start: number, end: number, bases: string, options?: {}): void {
    if (options == null) {
      options = {};
    }
    this.invalidateLineRanges();
    const seqLength = alignment.get('sequence').bases().length;
    const length = sequtil.length(start, end, seqLength);
    if (bases.length === length) {
      this._handleStrictReplace(alignment, start, end, bases, options);
      this.renderLineRanges(alignment);
    } else if (bases.length < length) {
      const mid = sequtil.mod(start + bases.length, seqLength);
      this._handleStrictReplace(alignment, start, mid, bases, options);
      this._handleDelete(alignment, mid, end);
    } else {
      this._handleStrictReplace(alignment, start, end, bases.slice(0, length), options);
      this._handleInsert(alignment, start + length, bases.slice(length), options);
    }
    alignment.get('sequence').trigger('revote');
    this.update(alignment);
  }

  _handleInsert(alignment: Alignment, pos: number, bases: string, options: {} | null): void {
    if (options == null) {
      options = {};
    }
    const others = this.without(alignment);
    const string = Array(bases.length + 1).join('-');
    others.forEach((other) => {
      other.get('sequence').insertBases(pos, string);
      const otherTrace = other.get('trace');
      if (otherTrace != null) {
        otherTrace.insert(pos, string);
      }
      other.get('sequence').trigger('inserted', pos, bases);
    });

    const sequence = alignment.get('sequence');
    // @ts-expect-error: Extra arg
    sequence.insertBases(pos, bases, options);
    sequence.trigger('inserted', pos, bases);
    const trace = alignment.get('trace');
    if (trace != null) {
      trace.insert(pos, bases);
    }
  }

  _handleDelete(alignment: Alignment, start: number, end: number): void {
    const sequence = alignment.get('sequence');
    const length = sequtil.length(start, end, sequence.bases().length);
    const string = Array(length + 1).join('-');
    sequence.replaceBases(start, end, string);
  }

  _handleStrictReplace(
    alignment: Alignment,
    start: number,
    end: number,
    bases: string,
    options: {} | null
  ): void {
    if (options == null) {
      options = {};
    }
    const sequence = alignment.get('sequence');
    const seqLength = sequence.length();
    assert(
      sequtil.length(start, end, seqLength) === bases.length,
      `handle strict replace lengths must be equal: ${start}, ${end}, ${sequence.length()}, ${bases}`
    );
    sequence.replaceBases(start, end, bases);
  }

  _handleRemovingGaps(start: number, end: number): void {
    const length = this.seqLength();
    if (start >= end) {
      end += length;
    }
    let gaps = [];
    for (const index of range(start, end - 1)) {
      if (this.every((al) => !al.hasDataAt(index))) {
        gaps.push(index);
      }
    }
    gaps = _.sortBy(gaps, (num) => -num);

    gaps.map((gap) =>
      this.each((alignment) => {
        alignment.get('sequence').deleteBases(gap, gap + 1);
        const trace = alignment.get('trace');
        if (trace != null) {
          trace.deleteIndex(gap);
        }
        alignment.get('sequence').trigger('deleted', gap, gap + 1);
      })
    );
  }
}
AlignmentCollection.initClass();

interface VoteDataAttributes {
  votes: Array<number>;
  sequence: GapSequence;
  voterSequences: Array<GapSequence>;
}

export interface VoteData {
  get<K extends keyof VoteDataAttributes>(key: K): VoteDataAttributes[K];
  set<K extends keyof VoteDataAttributes>(
    attributeName: K,
    value: VoteDataAttributes[K],
    options?: Backbone.ModelSetOptions
  ): VoteData;
  set<K extends keyof VoteDataAttributes>(obj: Partial<VoteDataAttributes>): VoteData;
}

export class VoteData extends Backbone.Model {
  defaults(): VoteDataAttributes {
    return {
      // @ts-expect-error: Initially null
      votes: null,
      // @ts-expect-error: Initially null
      sequence: null,
      voterSequences: [],
    };
  }

  initialize(attributes: {}, options: {}): void {
    this._calcVotes();
    const {length} = this.get('sequence').bases();
    for (const sequence of Array.from(this.get('voterSequences'))) {
      assert(sequence.bases().length === length, 'All sequences must be of the same length!');
    }

    for (const sequence of Array.from(this.get('voterSequences'))) {
      this.listenTo(sequence, 'revote', this._calcVotes.bind(this));
      this.listenTo(sequence, 'delete', this.handleDeleteSequence.bind(this));
    }

    this.listenTo(this.get('sequence'), 'revote', this._calcVotes.bind(this));
  }

  handleDeleteSequence(sequence: GapSequence): void {
    this.set(
      'voterSequences',
      this.get('voterSequences').filter((voterSequence: GapSequence) => voterSequence !== sequence)
    );
    this._calcVotes();
  }

  _calcVotes(): void {
    const votes = [];
    const sequence = this.get('sequence');
    const iterable = sequence.bases();
    for (let i = 0; i < iterable.length; i++) {
      const base = iterable[i];
      let count = 0;
      for (const voterSequence of Array.from(this.get('voterSequences'))) {
        let voteBase = voterSequence.bases()[i];
        // if sequence has a defined nucleotide type, sanitize votes to match.
        const nucleotideType = sequence.get('nucleotideType');
        if (nucleotideType) {
          voteBase = sanitize(voteBase, nucleotideType);
        }
        if (voteBase.toLowerCase() === base.toLowerCase()) {
          count++;
        }
      }
      votes.push(count);
    }
    this.set({votes});
    this.trigger('change');
  }
}

interface TraceDataAttributes extends Trace {
  // The upper limit for rendering color data. Any values above this should be rendered
  // equivalently at the ceiling of the line graph.
  maxY: number;
  alignedSequence: ComponentsSequence;
}

export interface TraceData {
  get<K extends keyof TraceDataAttributes>(key: K): TraceDataAttributes[K];
  set<K extends keyof TraceDataAttributes>(
    attributeName: K,
    value: TraceDataAttributes[K],
    options?: Backbone.ModelSetOptions
  ): TraceData;
  set(obj: Partial<TraceDataAttributes>): TraceData;
}

export class TraceData extends Backbone.Model {
  attributes!: Trace;

  defaults(): TraceDataAttributes {
    return {
      // @ts-expect-error: Initially null
      baseToColor: null,
      // @ts-expect-error: Initially null
      qualityScores: null,
      // @ts-expect-error: Initially null
      peakLocations: null,
      // @ts-expect-error: Initially null
      alignedSequence: null,
    };
  }

  getMaxTraceScore(): number {
    // eslint-disable-next-line no-restricted-syntax
    return _.max(_.flatten(_.values(this.get('baseToColor'))));
  }

  insert(pos: number, string: string): Array<number | null> {
    let array;
    const baseToColor = this.get('baseToColor');
    for (const color of Object.keys(baseToColor) as Array<StandardDNABase>) {
      const scores = baseToColor[color];
      array = [];
      for (let i = 0; i < string.length; i++) {
        array.push([]);
      }
      scores.splice(pos, 0, ...array);
    }
    array = [];
    for (let i = 0; i < string.length; i++) {
      array.push(null);
    }
    this.get('peakLocations').splice(pos, 0, ...array);
    array = [];
    for (let i = 0; i < string.length; i++) {
      array.push(null);
    }
    return this.get('qualityScores').splice(pos, 0, ...array);
  }

  deleteIndex(index: number): void {
    let remove: Array<number> = [];
    if (this.get('baseToColor') != null) {
      Object.values(this.get('baseToColor')).forEach((array: Array<Array<number>>) => {
        remove = array.splice(index, 1)[0];
      });
    }
    this.get('qualityScores').splice(index, 1);
    this.get('peakLocations').splice(index, 1);
    for (let i = index; i < this.get('peakLocations').length; i++) {
      this.get('peakLocations')[i]! -= remove.length;
    }
  }

  deleteIndices(start: number, end: number): void {
    let remove: Array<Array<number>> = [];
    Object.values(this.get('baseToColor')).forEach((colorArray: Array<Array<number>>) => {
      remove = colorArray.splice(start, end - start);
    });
    this.get('qualityScores').splice(start, end - start);
    this.get('peakLocations').splice(start, end - start);
    const {length} = remove.flat();
    for (let i = start; i < this.get('peakLocations').length; i++) {
      this.get('peakLocations')[i]! -= length;
    }
  }

  hasDataAt(index: number): boolean {
    return Object.values(this.get('baseToColor')).some((arr: Array<Array<number>>) => arr[index].length > 0);
  }

  cloneWithoutGaps(): TraceData {
    const baseToColor: {[key: string]: Array<Array<number>>} = {};
    for (const color of Object.keys(this.get('baseToColor')) as Array<StandardDNABase>) {
      baseToColor[color] = _.clone(this.get('baseToColor')[color]);
    }
    const clone = new TraceData({
      baseToColor,
      qualityScores: _.clone(this.get('qualityScores')),
      peakLocations: _.clone(this.get('peakLocations')),
    });
    let end = 0;
    let baseIndex = 0;
    let ret = this.get('alignedSequence').bases().slice(end).match(`${GAP}+`);
    while (ret != null) {
      baseIndex += ret.index!;
      const start = ret.index! + end;
      end = start + ret[0].length;
      clone.deleteIndices(baseIndex, baseIndex + ret[0].length);
      ret = this.get('alignedSequence').bases().slice(end).match(`${GAP}+`);
    }

    return clone;
  }

  toJSON(): Trace {
    // @ts-expect-error Keys are incorrect.
    return omit(this.attributes, 'alignmentComments', 'alignedSequence');
  }
}

export class Analyses<T extends Analysis> extends SequenceAttributeCollection<T> {
  static initClass(): void {
    // @ts-expect-error: Abstract class should not be assigned to non-abstract constructor
    this.prototype.model = Analysis;
    this.prototype.attributeName = 'analyses';
  }
}
Analyses.initClass();
